import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles} from '@mantine/core';
import React from 'react';

import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {ensureDefaultProcessesPluginRegistered} from 'processes/settings/ProcessTaskSchemaEditor';

import ExecutionFlowchartEditor from './ExecutionFlowchartEditor';
import {ExecutionFlowchartPaneProps} from './ExecutionFlowchartPaneTypes';
import {useExecutionFlowchartPaneReadiness} from './useExecutionFlowchartPaneReadiness';

export type {ExecutionFlowchartPaneProps} from './ExecutionFlowchartPaneTypes';

export const EXECUTION_FLOWCHART_PANE_ROOT_TEST_ID = getTestProps({
  component: 'ExecutionFlowchartPane',
  element: 'root',
})['data-testid'] as string;

const useExecutionFlowchartPaneStyles = createStyles(() => ({
  root: {
    height: '100%',
    width: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  editorShell: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}));

function paneDataAttributes(props: ExecutionFlowchartPaneProps): {
  'data-flowchart-config-id': string;
  'data-process-id': string;
} {
  switch (props.editorMode) {
    case FlowchartEditorMode.ACM_EDITOR:
      return {'data-flowchart-config-id': props.flowchartConfigId, 'data-process-id': ''};
    case FlowchartEditorMode.PCM_EDITOR:
      return {'data-flowchart-config-id': '', 'data-process-id': props.processId};
    case FlowchartEditorMode.EXECUTION_VIEWER:
      return {
        'data-flowchart-config-id': props.flowchartConfigId ?? '',
        'data-process-id': props.processId ?? '',
      };
    default: {
      const _exhaustive: never = props;
      return _exhaustive;
    }
  }
}

/**
 * Entry component for execution-style flowcharts: wires draft/readiness props into
 * {@link useExecutionFlowchartPaneReadiness} and {@link ExecutionFlowchartEditor}. Used from
 * process modals and task-group execution viewer.
 */
export default function ExecutionFlowchartPane(props: ExecutionFlowchartPaneProps): JSX.Element {
  ensureDefaultProcessesPluginRegistered({ignoreFlowchartConfigFlag: true});
  const {classes} = useExecutionFlowchartPaneStyles();

  const readiness = useExecutionFlowchartPaneReadiness({
    editorMode: props.editorMode,
    readinessSnapshot: props.readinessSnapshot,
    draftState: props.draftState,
    setDraftState: props.setDraftState,
    onBlockingErrorsChange: props.onBlockingErrorsChange,
    onNodeNavigateClick: props.onNodeNavigateClick,
    navigableNodeConfigIds: props.navigableNodeConfigIds,
    selectedNodeConfigId: props.selectedNodeConfigId,
  });

  return (
    <Box
      className={classes.root}
      {...getTestProps({component: 'ExecutionFlowchartPane', element: 'root'})}
      data-editor-mode={props.editorMode}
      {...paneDataAttributes(props)}
    >
      <Box className={classes.editorShell}>
        <ExecutionFlowchartEditor readiness={readiness} />
      </Box>
    </Box>
  );
}
